import { CurecallCheckbox, CurecallMultiSelect, ConfigIcon, useClickOutside } from '@honestica/curecall-ui'
import type { CurecallMultiSelectItem } from '@honestica/curecall-ui'
import { useTranslation } from 'react-i18next'
import { useDashboard } from './dashboard.provider'
import type { TDashboardPatientMedEvent } from './dashboard.type'
import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react'
import { useCurrentProject } from 'src/providers/current-project.provider'

const DashboardHeader = () => {
  const { t } = useTranslation()
  const { project, getMedEventLabelById, locations = [] } = useCurrentProject()

  const [showReviewedPatientMedEvents, setShowReviewedPatientMedEvents] = useState<boolean>(false)
  const [selectedMedEventOptions, setSelectedMedEventOptions] = useState<CurecallMultiSelectItem[]>([])
  const [selectedMedEventIds, setSelectedMedEventIds] = useState<number[]>([])
  const [selectedLocationOptions, setSelectedLocationOptions] = useState<CurecallMultiSelectItem[]>([])
  const [selectedLocationIds, setSelectedLocationIds] = useState<number[]>([])
  const [isFiltersOpen, setIsFiltersOpen] = useState(false)
  const filtersRef = useRef(null)

  const hasMultipleFilterTypes = locations.length > 1

  const {
    dashboardPatientMedEventList,
    setDashboardPatientMedEventListToShow,
    dashboardMedEventIds,
    selectedPatientMedEvent,
    setSelectedPatientMedEvent,
  } = useDashboard()

  const AVAILABLE_MEDEVENTS = useMemo(
    () =>
      dashboardMedEventIds
        .map((medEventId: number) => ({
          label: getMedEventLabelById(medEventId),
          value: medEventId,
        }))
        .sort((m1, m2) => m1.label.localeCompare(m2.label)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(dashboardMedEventIds)],
  )

  const AVAILABLE_LOCATIONS = useMemo(
    () =>
      locations
        .map((location) => ({ label: location.name, value: location.id }))
        .sort((l1, l2) => l1.label.localeCompare(l2.label)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(locations)],
  )

  const filterByFilters = ({
    filterByMedEventIds,
    filterByLocationIds,
    showReviewedPatientMedEvents,
  }: {
    filterByMedEventIds?: number[]
    filterByLocationIds?: number[]
    showReviewedPatientMedEvents?: boolean
  }) => {
    let result = dashboardPatientMedEventList
    if (filterByMedEventIds?.length) {
      result = result.filter((patientMedEvent) => filterByMedEventIds?.includes(patientMedEvent.MedEventId))
    }
    if (filterByLocationIds?.length) {
      result = result.filter(
        (patientMedEvent) =>
          patientMedEvent.LocationId !== undefined &&
          filterByLocationIds.includes(patientMedEvent.LocationId),
      )
    }
    if (!showReviewedPatientMedEvents) {
      result = result.filter((patientMedEvent) => patientMedEvent.status !== 'REVIEWED')
    }
    return result
  }

  const numberToBeReviewed = dashboardPatientMedEventList
    .filter((patientMedEvent: TDashboardPatientMedEvent) => patientMedEvent.status !== 'REVIEWED')
    .filter((patientMedEvent) =>
      selectedMedEventIds.length ? selectedMedEventIds.includes(patientMedEvent.MedEventId) : patientMedEvent,
    )
    .filter((patientMedEvent) =>
      selectedLocationIds.length
        ? patientMedEvent.LocationId !== undefined && selectedLocationIds.includes(patientMedEvent.LocationId)
        : patientMedEvent,
    ).length
  const numberReviewed = dashboardPatientMedEventList
    .filter((patientMedEvent: TDashboardPatientMedEvent) => patientMedEvent.status === 'REVIEWED')
    .filter((patientMedEvent) =>
      selectedMedEventIds.length ? selectedMedEventIds.includes(patientMedEvent.MedEventId) : patientMedEvent,
    )
    .filter((patientMedEvent) =>
      selectedLocationIds.length
        ? patientMedEvent.LocationId !== undefined && selectedLocationIds.includes(patientMedEvent.LocationId)
        : patientMedEvent,
    ).length

  useEffect(() => {
    setDashboardPatientMedEventListToShow(
      filterByFilters({
        filterByMedEventIds: selectedMedEventIds,
        filterByLocationIds: selectedLocationIds,
        showReviewedPatientMedEvents,
      }),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedMedEventIds,
    selectedLocationIds,
    showReviewedPatientMedEvents,
    JSON.stringify(dashboardPatientMedEventList),
  ])

  useClickOutside({ ref: filtersRef, callBack: () => setIsFiltersOpen(false) })

  const onChangeReviewedFilter = (event: ChangeEvent<HTMLInputElement>) => {
    setShowReviewedPatientMedEvents(event.target.checked)
  }

  const onChangeMedEventFilter = (givenOptions: CurecallMultiSelectItem[]) => {
    setSelectedMedEventOptions(givenOptions)
    const newSelectedMedEventIds = givenOptions.map((option) => option.value)
    setSelectedMedEventIds(newSelectedMedEventIds)
    const filteredList = filterByFilters({
      filterByMedEventIds: newSelectedMedEventIds,
      filterByLocationIds: selectedLocationIds,
      showReviewedPatientMedEvents,
    })
    const shouldUnselectCurrentPatientMedEvent = !newSelectedMedEventIds.includes(
      selectedPatientMedEvent?.MedEventId,
    )
    if (shouldUnselectCurrentPatientMedEvent && filteredList?.[0]) {
      setSelectedPatientMedEvent(filteredList[0])
    }
  }

  const onChangeLocationFilter = (givenOptions: CurecallMultiSelectItem[]) => {
    setSelectedLocationOptions(givenOptions)
    const newSelectedLocationIds = givenOptions.map((option) => option.value)
    setSelectedLocationIds(newSelectedLocationIds)
    const filteredList = filterByFilters({
      filterByMedEventIds: selectedMedEventIds,
      filterByLocationIds: newSelectedLocationIds,
      showReviewedPatientMedEvents,
    })
    const shouldUnselectCurrentPatientMedEvent =
      newSelectedLocationIds.length > 0 &&
      (selectedPatientMedEvent?.LocationId === undefined ||
        !newSelectedLocationIds.includes(selectedPatientMedEvent.LocationId))
    if (shouldUnselectCurrentPatientMedEvent && filteredList?.[0]) {
      setSelectedPatientMedEvent(filteredList[0])
    }
  }

  const onResetFilters = () => {
    setSelectedMedEventIds([])
    setSelectedMedEventOptions([])
    setSelectedLocationIds([])
    setSelectedLocationOptions([])
  }

  const activeFiltersCount = selectedMedEventIds.length + selectedLocationIds.length

  return (
    <header>
      <h1 className="c-hidden c-p-0 c-pb-4 c-font-nexa c-text-2xl c-m-0">
        {t('pages.dashboard.dashboard-title', { name: project.name })}
      </h1>
      <div className="c-flex c-items-center c-mt-2 c-gap-6">
        <p className="c-p-0 c-m-0 c-text-sm xl:c-text-base c-font-bold c-tracking-wider c-font-nexa">
          {t('pages.dashboard.stats-title', { number: numberToBeReviewed })}
        </p>
        <CurecallCheckbox
          labelClassName="!c-text-sm xl:!c-text-base tw-font-bold tw-tracking-wider"
          label={t('pages.dashboard.show-reviewed-patients', {
            number: numberReviewed,
          })}
          onChange={onChangeReviewedFilter}
          checked={showReviewedPatientMedEvents}
        />
        {hasMultipleFilterTypes ? (
          <div className="c-relative" ref={filtersRef}>
            <button
              onClick={() => setIsFiltersOpen(!isFiltersOpen)}
              className="c-flex c-items-center c-gap-2 c-text-primary c-font-bold hover:c-underline hover:c-text-primary-500"
              type="button"
            >
              <ConfigIcon className="c-w-4" />
              {t('pages.dashboard.filters', { count: activeFiltersCount })}
            </button>
            {isFiltersOpen && (
              <div className="c-absolute c-top-full c-left-0 c-bg-grey-50 c-border c-border-grey-500 c-rounded-lg c-shadow-lg c-p-4 c-pr-10 c-z-30 c-min-w-max">
                <CurecallMultiSelect
                  label={t('pages.configuration.filter-by-medevent')}
                  labelClassName="tw-p-0 tw-m-0"
                  inputClassName="tw-h-8 [&_li]:c-h-6 [&_li]:c-p-1 tw-w-72"
                  wrapperClassName="tw-relative"
                  items={AVAILABLE_MEDEVENTS}
                  selectedItems={selectedMedEventOptions}
                  selectedElementsLabel={
                    selectedMedEventIds.length + ' ' + t('pages.configuration.messages.selected')
                  }
                  placeholder={t('pages.configuration.choose-a-medevent')}
                  onSelect={onChangeMedEventFilter}
                />
                <CurecallMultiSelect
                  label={t('pages.dashboard.filter-by-location')}
                  labelClassName="tw-p-0 tw-m-0 tw-mt-3"
                  inputClassName="tw-h-8 [&_li]:c-h-6 [&_li]:c-p-1 tw-w-72"
                  wrapperClassName="tw-relative"
                  items={AVAILABLE_LOCATIONS}
                  selectedItems={selectedLocationOptions}
                  selectedElementsLabel={t('pages.dashboard.location-selected', {
                    number: selectedLocationIds.length,
                  })}
                  placeholder={t('pages.dashboard.choose-a-location')}
                  onSelect={onChangeLocationFilter}
                />
                {activeFiltersCount > 0 && (
                  <button
                    className="c-text-primary c-font-bold hover:c-underline c-ml-1 c-mb-2 c-mt-4"
                    onClick={onResetFilters}
                    type="button"
                  >
                    {t('pages.planning.filters.reset-filters')}
                  </button>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="c-flex c-items-center c-gap-4 c-w-[630px]">
            <CurecallMultiSelect
              label={t('pages.configuration.filter-by-medevent')}
              labelClassName="tw-whitespace-nowrap tw-w-max"
              wrapperClassName="c-relative c-flex c-items-center c-gap-2 c-w-full"
              optionsClassName="tw-w-96 tw-top-10 tw-right-0"
              items={AVAILABLE_MEDEVENTS}
              selectedItems={selectedMedEventOptions}
              selectedElementsLabel={
                selectedMedEventIds.length + ' ' + t('pages.configuration.messages.selected')
              }
              placeholder={t('pages.configuration.choose-a-medevent')}
              onSelect={onChangeMedEventFilter}
            />
            {selectedMedEventIds.length > 0 && (
              <button
                onClick={onResetFilters}
                className="tw-shrink-0 tw-font-nexa tw-font-semibold tw-text-primary hover:tw-text-primary-800"
              >
                {t('pages.configuration.clear-filter')}
              </button>
            )}
          </div>
        )}
      </div>
    </header>
  )
}

export default DashboardHeader
